import {isArray} from 'lodash';
import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {vinRe} from '@deliveriesondemand/dod-regexes';

import SplitStyle from './SplitStyle';
import Select from '-/components/fields/Select';
import {getNADAStyles} from '-/api/api';
import {Control, ControlGroup} from '-/components/fields/Control';

const Trim = props => {
    const {contactPhone, handleAccessoriesChange, handleChange, showErrors, vehicleInfo = {}, trims = []} = props;
    const {
        make,
        model,
        style: incomingStyle,
        ucgVehicleId,
        vin,
        year
    } = vehicleInfo;

    const [lookupFailed, setLookupFailed] = useState(false);
    const [styleOpts, setStyleOpts] = useState([]);
    const [showSplitStyle, setShowSplitStyle] = useState(false);
    const [splitStyleOpts, setSplitStyleOpts] = useState([]);
    const [style, setStyle] = useState(incomingStyle);
    const [selectedOpts, setSelectedOpts] = useState({});
    const invalidCSSStyle = !style && showErrors ? {border: '1px solid red'} : {};
    const isVinValid =  vin && vinRe.test(vin);

    useEffect(() => {
        if (style || trims.length !== 1) {
            return;
        }

        const {body: styleDesc, ucgVehicleId: newUcgVehicleId, vid} = trims[0];

        setStyleOpts(trims);
        handleChange({style: vid, styleDesc, ucgVehicleId: newUcgVehicleId});
        handleAccessoriesChange([]);
        setStyle(vid);
    }, [trims, style]);

    const getStyles = async(opts = {}) => {
        const shouldLookup = !isVinValid && (year && make && model);

        if (shouldLookup) {
            try {
                const {year, make, model} = opts;
                const newStyleOpts = await getNADAStyles({year, make, model});

                if (isArray(newStyleOpts)) {
                    if (newStyleOpts.length > 0) {
                        setStyleOpts(newStyleOpts);
                    } else {
                        setLookupFailed(true);
                    }
                } else {
                    const {splitTrims: newSplitStyleOpts = [], trims = []} = newStyleOpts;

                    if (newSplitStyleOpts.length > 1) {
                        setStyleOpts(trims);
                        setSplitStyleOpts(newSplitStyleOpts);
                        setShowSplitStyle(true);
                    } else {
                        setStyleOpts(trims);
                        setShowSplitStyle(false);
                    }
                }
            } catch (ex) {
                setLookupFailed(true);
            }
        }
    };

    const handleStyleChange = styleId => {
        if (styleId) {
            const selectedOpt = styleOpts.find(opt => opt.vid === styleId);
            if (!selectedOpt) {
                return;
            }

            const {body: styleDesc, ucgVehicleId: newUcgVehicleId, vid} = selectedOpt;

            handleChange({
                style: vid,
                styleDesc,
                ucgVehicleId: newUcgVehicleId
            });
            handleAccessoriesChange([]);
            setStyle(vid);
        }
    };

    const cleanSelections = (selectedOpts, fullText) => {
        Object.keys(selectedOpts).forEach(key => {
            if (!fullText.includes(key)) {
                delete selectedOpts[key];
            }

            if (selectedOpts[key]) {
                cleanSelections(selectedOpts[key], fullText);
            }
        });

        return selectedOpts;
    };

    const handleOptsListChange = (newSelectedOpts, opt) => {
        const {fullText, leaf} = opt;
        const selections = cleanSelections(newSelectedOpts, fullText);

        setSelectedOpts(selections);

        if (leaf) {
            const vehicleInfo = styleOpts.find(opt => opt.body === fullText);

            if (vehicleInfo) {
                const {vid} = vehicleInfo;

                vehicleInfo.style = vid;

                handleChange(vehicleInfo);
                handleAccessoriesChange([]);
                setStyle(vid);
            }
        } else {
            handleChange({});
        }
    };

    useEffect(() => {
        getStyles({year, make, model});
    }, [make, model, year]);

    useEffect(() => {
        setStyleOpts(trims);
    }, [vin, trims.length]);

    return (
        <Fragment>
            {lookupFailed && (
                <p className="mt-3 alert alert-danger">
                    There was an issue with the lookup. Please go
                    back and try a different method.  If the problem persists,
                    please contact us at: {contactPhone}
                </p>
            )}
            {(showSplitStyle && splitStyleOpts.length > 0 && !lookupFailed) && (
                <SplitStyle
                    options={splitStyleOpts}
                    onChange={handleOptsListChange}
                    selectedOptions={selectedOpts} />
            )}
            {(!showSplitStyle && !lookupFailed) && (
                <ControlGroup labelWidth={200}>
                    <Control>
                        <Select
                            label="Trim"
                            style={invalidCSSStyle}
                            readOnly
                            onChange={handleStyleChange}>
                            <option>Select a Trim Package</option>
                            {styleOpts.map((styleObj, i) => {
                                const {body, ucgVehicleId: newUcgVehicleId, vid} = styleObj;

                                return (
                                    <option
                                        key={`${newUcgVehicleId}-${i}`}
                                        selected={style === vid || newUcgVehicleId === ucgVehicleId}
                                        value={vid}>
                                        {body}
                                    </option>
                                );
                            })}
                        </Select>
                    </Control>
                </ControlGroup>
            )}
        </Fragment>
    );
};

Trim.propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleAccessoriesChange: PropTypes.func.isRequired,
    showErrors: PropTypes.bool
};

export default Trim;
